import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingSchedulerService } from 'src/app/services/meeting-sheduler';
import { firstValueFrom } from 'rxjs';
import { createDefaultForm, setupFormFields, processTimeSlots, generateWeekDaysArray, formatDurationText } from './helper/helper-meeting';
import { prefixes } from 'src/app/leads/interface-data/data';
import { SelectComponent } from '../select/select.component';
import { MaterialModule } from 'src/app/material.module';
import { endOfWeek, startOfWeek } from 'date-fns';
import { RecaptchaV3Service } from 'src/app/services/recaptcha-v3.service';
import { RecaptchaService } from 'src/app/services/recaptcha.service'; // Add this import
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

interface TimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
  formattedTime: string;
}

interface DayInfo {
  date: Date;
  dayName: string;
  dayNumber: number;
  month: string;
  isToday: boolean;
  isSelected: boolean;
  hasSlots: boolean;
}

interface Prefix {
  country: string;
  prefix: string;
  flag: string;
}

interface Field {
  id: string;
  label: string;
  type: string;
  isRequired: boolean;
  isDefault?: boolean;
  options?: string;
  prefillValue?: string;
  isLeadField?: boolean;
  showInScheduler?: boolean;
  minValue?: number;
  maxValue?: number;
  step?: number;
  minDate?: string;
  maxDate?: string;
  key?: string;
}

// Session Storage keys
const SESSION_MEETING_KEY = 'meeting_data_';
const SESSION_TIMESLOT_KEY = 'meeting_timeslot_';
const SESSION_EXPIRY_KEY = 'meeting_expiry_';

@Component({
  selector: 'app-meeting-scheduler',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SelectComponent, MaterialModule, MatSnackBarModule],
  templateUrl: './meeting-scheduler.component.html',
  styleUrl: './meeting-scheduler.component.scss'
})
export class MeetingSchedulerComponent implements OnInit, OnDestroy {
  @Input() meetingId: string = '';
  
  // Date and selection related properties
  currentDate = new Date();
  selectedDate: Date | null = null;
  weekDays: DayInfo[] = [];
  
  // Meeting data and time slots
  meetingData: any = null;
  morningSlots: TimeSlot[] = [];
  afternoonSlots: TimeSlot[] = [];
  selectedTimeSlot: TimeSlot | null = null;
  
  // Form related properties
  showBookingForm = false;
  bookingForm: FormGroup;
  defaultFields: Field[] = [];
  customFields: Field[] = [];
  leadFields: Field[] = [];
  isLeadCreationEnabled: boolean = false;
  
  // Phone prefix related properties
  prefixes: Prefix[] = prefixes;
  selectedPrefix: Prefix = this.prefixes[1]; // Default to USA/CAD
  showPrefixDropdown = false;
  meetingLinkExpired = false;
  
  // Thank you page
  bookingComplete = false;
  bookedMeetingDetails: any = null;
  isThankYouPage = false;
  disableButton : boolean = false;
  disablePreviousButton: boolean = false;
  disableNextButton: boolean = false;
  isLoadingWeek: boolean = false;

  // reCAPTCHA related
  recaptchaRequired: boolean = true;
  recaptchaInitialized: boolean = false;

  allPrograms: any[] = [];
  allCampuses: any[] = [];
  filteredPrograms: string[] = [];
  campusProgramMapping: Map<number, number[]> = new Map();
  
  // Phone validation patterns
  phonePatterns = {
    '+1': /^[2-9]\d{2}[2-9]\d{2}\d{4}$/,  // US/Canada format: XXX-XXX-XXXX
    '+44': /^\d{10}$/,                    // UK format: 10 digits
    '+61': /^\d{9}$/,                     // Australia: 9 digits
    '+91': /^[6-9]\d{9}$/,                // India: 10 digits starting with 6-9
    'default': /^\d{4,15}$/               // Generic format: minimum 4 digits, max 15
  };

  scheduleId: string;
  cancelId: string;
  isRescheduling = false;
  
  // Cached time slots by date
  private timesByDate: Record<string, TimeSlot[]> = {};

  userTimezone: string = '';
  userTimezoneOffset: string = '';
  meetingTimezone: string = '';
  meetingTimezoneOffset: string = '';
  
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private meetingService: MeetingSchedulerService,
    private recaptchaV3Service: RecaptchaV3Service,
    private recaptchaService: RecaptchaService, // Add this
    private snackBar: MatSnackBar
  ) {
    this.bookingForm = this.createBookingForm();
  }
  
  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.prefix-dropdown')) {
      this.showPrefixDropdown = false;
    }
  }
  
  ngOnInit(): void {
    // Get meetingId from route if not provided as input
    this.initializeTimezoneInfo();
    this.updateNavigationStates();

    // Check if URL contains thank-you-page
    const url = this.router.url;
    this.isThankYouPage = url.includes('/thank-you-page');
    
    this.route.params.subscribe(params => {
      this.meetingId = params['id'];
      if(params['scheduleId']){
        this.scheduleId = params['scheduleId'];
        this.isRescheduling = true;  // Set reschedule mode if scheduleId is present
      }
      if(params['cancelScheduleId']){
        this.cancelId = params['cancelScheduleId'];
      }
      
      if (this.isThankYouPage) {
        this.loadFromSessionStorage();
      } else {
        this.loadMeetingData();
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up if needed
  }

  // Save meeting data and time slot to session storage with expiration
  saveToSessionStorage(meetingData: any, timeSlot: TimeSlot | null, formData: any): void {
    // Create a 2-hour expiry timestamp
    const expiryTime = new Date();
    expiryTime.setHours(expiryTime.getHours() + 2);
    
    // Store the meeting data
    sessionStorage.setItem(SESSION_MEETING_KEY + this.meetingId, JSON.stringify(meetingData));
    
    // Store the time slot, fixing date serialization
    const timeSlotToStore = timeSlot ? {
      ...timeSlot,
      startTime: timeSlot.startTime.toISOString(),
      endTime: timeSlot.endTime.toISOString()
    } : null;
    
    sessionStorage.setItem(SESSION_TIMESLOT_KEY + this.meetingId, JSON.stringify({
      timeSlot: timeSlotToStore,
      formData: formData
    }));
    
    // Store the expiry timestamp
    sessionStorage.setItem(SESSION_EXPIRY_KEY + this.meetingId, expiryTime.toISOString());
  }

  // Load data from session storage and check expiration
  loadFromSessionStorage(): void {
    // Check for expiry time
    const expiryTimeStr = sessionStorage.getItem(SESSION_EXPIRY_KEY + this.meetingId);
    if (!expiryTimeStr) {
      // No expiry time found - redirect back to scheduler
      this.redirectToScheduler();
      return;
    }
    
    // Check if the data has expired
    const expiryTime = new Date(expiryTimeStr);
    const currentTime = new Date();
    if (currentTime > expiryTime) {
      // Data has expired - clear it and redirect
      this.clearSessionStorage();
      this.redirectToScheduler();
      return;
    }
    
    // Load the meeting data
    const meetingDataStr = sessionStorage.getItem(SESSION_MEETING_KEY + this.meetingId);
    const timeSlotDataStr = sessionStorage.getItem(SESSION_TIMESLOT_KEY + this.meetingId);
    
    if (!meetingDataStr || !timeSlotDataStr) {
      this.redirectToScheduler();
      return;
    }
    
    try {
      // Parse the meeting data
      this.meetingData = JSON.parse(meetingDataStr);
      
      // Parse the time slot data
      const timeSlotData = JSON.parse(timeSlotDataStr);
      
      // Reconstruct the TimeSlot object with proper Date objects
      if (timeSlotData.timeSlot) {
        this.selectedTimeSlot = {
          ...timeSlotData.timeSlot,
          startTime: new Date(timeSlotData.timeSlot.startTime),
          endTime: new Date(timeSlotData.timeSlot.endTime)
        };
      }
      
      // Set booking complete flag
      this.bookingComplete = true;
      
      // Set up booked meeting details
      this.bookedMeetingDetails = {
        meeting: this.meetingData,
        formData: timeSlotData.formData,
        timeSlot: this.selectedTimeSlot
      };
      
    } catch (error) {
      console.error('Error parsing session data:', error);
      this.redirectToScheduler();
    }
  }
  
  // Clear session storage for this meeting
  clearSessionStorage(): void {
    sessionStorage.removeItem(SESSION_MEETING_KEY + this.meetingId);
    sessionStorage.removeItem(SESSION_TIMESLOT_KEY + this.meetingId);
    sessionStorage.removeItem(SESSION_EXPIRY_KEY + this.meetingId);
  }
  
  // Redirect to the regular scheduler page
  redirectToScheduler(): void {
    this.router.navigate([`/meeting-schedule/${this.meetingId}`]);
  }

  updateNavigationStates(): void {
    // Get the start of the current week and selected week
    const today = new Date();
    const currentWeekStart = startOfWeek(today);
    const selectedWeekStart = startOfWeek(this.currentDate);
    
    // Disable previous week if selected week is current week or earlier
    this.disablePreviousButton = selectedWeekStart.getTime() <= currentWeekStart.getTime();
  }

  initializeTimezoneInfo(): void {
    // Get user's current timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.userTimezone = timezone;
    
    // Get timezone offset in format +/-HH:MM
    const now = new Date();
    const offsetMinutes = now.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
    const offsetMins = Math.abs(offsetMinutes % 60);
    const offsetSign = offsetMinutes <= 0 ? '+' : '-';
    
    this.userTimezoneOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  }
  
  createBookingForm(): FormGroup {
    // Basic form with empty controls - we'll add fields dynamically
    return this.fb.group({});
  }

  // Helper method to split phone number and prefix
  splitPhoneNumber(phoneValue: string): { prefix: string, number: string } {
    if (!phoneValue) {
      return { prefix: '+1', number: '' };
    }
    // Find matching prefix
    for (const prefixObj of this.prefixes) {
      if (phoneValue.startsWith(prefixObj.prefix)) {
        // Extract the number part after the prefix
        const numberPart = phoneValue.substring(prefixObj.prefix.length);
        return { prefix: prefixObj.prefix, number: numberPart };
      }
    }
    
    // If no prefix found, assume it's just a number without prefix
    return { prefix: '+1', number: phoneValue };
  }
  
  // Process meeting fields into form controls and field arrays
  setupFields(meetingFields: any[]): void {
    if (!meetingFields || !meetingFields.length) return;
    
    // Clear existing fields
    this.defaultFields = [];
    this.customFields = [];
    this.leadFields = [];
    
    // Check if lead creation is enabled
    this.isLeadCreationEnabled = this.meetingData?.createLead || false;
    
    // Create a new form
    const formGroup = this.createBookingForm();
    
    // Process each field from meeting data
    meetingFields.forEach((field, index) => {
      const fieldId = `field_${index}`;
      const isRequired = field.isRequired || false;
      const fieldType = field.type || field.fieldType || 'text';
      const isLeadField = field.isLeadField || false;
      const showInScheduler = field.showInScheduler !== false;
      
      if (!showInScheduler) return;
      
      if ((this.isLeadCreationEnabled && !isLeadField) || 
          (!this.isLeadCreationEnabled && isLeadField)) {
        return;
      }
      
      const validators = [];
      if (isRequired) validators.push(Validators.required);
      
      // Set default value
      let defaultValue = field.prefillValue || '';
      
      // Add field-specific validators
      if (fieldType === 'email') {
        validators.push(Validators.email);
      } else if (fieldType === 'tel') {
        // Handle phone field - split prefix and number if in edit mode
        if (defaultValue) {
          const { prefix, number } = this.splitPhoneNumber(defaultValue);
          formGroup.addControl(`${fieldId}Prefix`, this.fb.control(prefix));
          defaultValue = number; // Set the number part as the default value
          
          // Update selected prefix to match
          const matchingPrefix = this.prefixes.find(p => p.prefix === prefix);
          if (matchingPrefix) {
            this.selectedPrefix = matchingPrefix;
          }
        } else {
          formGroup.addControl(`${fieldId}Prefix`, this.fb.control('+1'));
        }
        validators.push(this.phoneValidator());
      } else if (fieldType === 'date') {
        // Add date validators
        validators.push(this.dateMinValidator(field.minDate));
        if (field.maxDate) {
          validators.push(this.dateMaxValidator(field.maxDate));
        }
      } else if (fieldType === 'number') {
        validators.push(this.numberRangeValidator(field.minValue, field.maxValue));
        // Graduation year: max 4 digits, no commas
        if (field.key === 'highSchoolYearGraduation') {
          validators.push(this.graduationYearValidator());
        }
      }
      
      // Add control to form
      formGroup.addControl(fieldId, this.fb.control(defaultValue, validators));
      
      // Add change listener for campus field (existing code)
      if (field.label.toLowerCase().includes('campus') && fieldType === 'select') {
        formGroup.get(fieldId)?.valueChanges.subscribe(value => {
          this.onCampusChange(value);
        });
      }
      
      const fieldObj: Field = {
        id: fieldId,
        label: field.label || field.fieldName || `Field ${index + 1}`,
        type: fieldType,
        key: field.key || '',   // <-- add this
        isRequired: isRequired,
        isDefault: field.isDefault || false,
        options: field.options || '',
        prefillValue: field.prefillValue || '',
        isLeadField: isLeadField,
        showInScheduler: showInScheduler,
        // New properties for date and number fields
        minValue: field.minValue,
        maxValue: field.maxValue,
        step: field.step || (fieldType === 'number' ? 1 : undefined),
        minDate: field.minDate,
        maxDate: field.maxDate
      };
      
      if (isLeadField) {
        this.leadFields.push(fieldObj);
      } else if (fieldObj.isDefault) {
        this.defaultFields.push(fieldObj);
      } else {
        this.customFields.push(fieldObj);
      }
    });
    
    this.bookingForm = formGroup;
  }
  
  // Add helper methods for template
  getFieldMin(field: Field): number | undefined {
    return field.minValue;
  }
  
  getFieldMax(field: Field): number | undefined {
    return field.maxValue;
  }
  
  getFieldStep(field: Field): number {
    return field.step || 1;
  }
  
  // Helper to determine if a field is default by name
  isDefaultFieldByName(fieldName: string): boolean {
    if (!fieldName) return false;
    const normalizedName = fieldName.toLowerCase().trim();
    return ['name', 'email', 'phone'].includes(normalizedName);
  }

  getPrefixForPhoneField(fieldId: string): string {
    const prefixControl = this.bookingForm.get(`${fieldId}Prefix`);
    return prefixControl ? prefixControl.value : '+1';
  }

  // Completely rewritten phoneValidator method
  phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return control.errors; // Return existing errors if empty
      }

      const raw = control.value as string || '';

      if (/[^0-9\s()+-]/.test(raw)) {
        return { invalidCharacters: true };
      }
      
      // We need to get the form group that contains this control
      const formGroup = control.parent as FormGroup;
      if (!formGroup) return null;
      
      // Find the control name (field ID) by searching in the parent FormGroup
      let fieldId: string | null = null;
      for (const key in formGroup.controls) {
        if (formGroup.controls[key] === control) {
          fieldId = key;
          break;
        }
      }
      
      if (!fieldId) return null;
      
      // Get the prefix value from the corresponding prefix control
      const prefixControlName = `${fieldId}Prefix`;
      const prefixValue = formGroup.get(prefixControlName)?.value || '+1';
      
      // Remove non-digit characters for validation
      const phoneValue = control.value.toString().replace(/\D/g, '');
      
      // Get the appropriate pattern based on the prefix
      const pattern = this.phonePatterns[prefixValue] || this.phonePatterns.default;
      
      if (!pattern.test(phoneValue)) {
        return { pattern: true };
      }
      
      return null;
    };
  }
  
  // Helper to split options string into array
  getFieldOptions(field: Field): string[] {
    if (!field.options) return [];
    return field.options.split(',').map(opt => opt.trim()).filter(opt => opt !== '');
  }
  
  async loadMeetingData() {
    if (!this.meetingId) return;
    
    try {
      const startWeek = startOfWeek(this.currentDate);
      const endWeek = endOfWeek(this.currentDate);
      const data = await firstValueFrom(this.meetingService.getMeetingScheduler({
        id: this.meetingId, 
        startWeek: startWeek, 
        endWeek: endWeek,
        rescheduleEvent: this.scheduleId,
        cancelEvent: this.cancelId
      }));

      this.meetingData = data;
      this.isLeadCreationEnabled = data.createLead || false;
      
      // Meeting scheduler is always a public-facing component
      this.recaptchaRequired = true;
      
      // Initialize reCAPTCHA v3 after meeting data is loaded
      if (this.recaptchaRequired && !this.recaptchaInitialized) {
        await this.initializeRecaptcha();
      }
      
      if (data.timezone) {
        this.meetingTimezone = data.timezone;
        this.meetingTimezoneOffset = data.timezoneOffset || '';
      }

      // Store campus and program data
      if (data.campuses) {
        this.allCampuses = data.campuses;
        this.buildCampusProgramMapping();
      }
      
      if (data.programs) {
        this.allPrograms = data.programs;
        // Initially show all programs
        this.filteredPrograms = this.allPrograms.map(p => p.name);
      }

      // Process fields from meeting data
      let processedFields = data.fields || [];
      
      // Check if campus field exists
      const hasCampusField = processedFields.some(field => 
        field.label.toLowerCase().includes('campus') && 
        field.showInScheduler !== false
      );
      
      // Update field options
      if (data.campuses || data.programs) {
        processedFields = processedFields.map(field => {
          let updatedField = { ...field };
          
          // Update campus options
          if (field.label.toLowerCase().includes('campus') && field.type === 'select' && data.campuses) {
            updatedField.options = data.campuses.map(campus => campus.name).join(', ');
          }
          
          // Update program options
          if (field.label.toLowerCase().includes('program') && field.type === 'select' && data.programs) {
            // If there's no campus field, show all programs
            // Otherwise, programs will be filtered based on campus selection
            if (!hasCampusField) {
              updatedField.options = data.programs.map(program => program.name).join(', ');
            } else {
              // Initially empty or show all programs
              updatedField.options = data.programs.map(program => program.name).join(', ');
            }
          }
          
          return updatedField;
        });
      }
      
      // Process all fields into form controls
      this.setupFields(processedFields);
      
      // Process time slots and set up the calendar
      if (data.is_general) {
        this.timesByDate = processTimeSlots(data.availableTimeSlots);
        if(data.availableTimeSlots.length == 0){
          this.disableNextButton = true;
        }
      } else {
        this.timesByDate = processTimeSlots(data.proposedTimes);
      }
      
      // Generate week days after processing time slots
      this.generateWeekDays();
      this.updateNavigationStates();
    } catch (error) {
      this.meetingLinkExpired = true;
      this.updateNavigationStates();
    }
  }

  private async initializeRecaptcha(): Promise<void> {
    try {
      // Pre-load reCAPTCHA v3 by executing it once
      await this.recaptchaV3Service.execute('page_load');
      this.recaptchaInitialized = true;
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA v3:', error);
      // Don't fail the meeting load if reCAPTCHA fails to initialize
      // But log it and potentially disable submission
      this.recaptchaRequired = false;
    }
  }

  isCampusField(field: Field): boolean {
    return field.label.toLowerCase().includes('campus') && field.type === 'select';
  }

  // Helper method to check if a field is a program field
  isProgramField(field: Field): boolean {
    return field.label.toLowerCase().includes('program') && field.type === 'select';
  }
  
  generateWeekDays(): void {
    const today = new Date();
    this.weekDays = generateWeekDaysArray(this.currentDate, today, this.timesByDate);
  }
  
  togglePrefixDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showPrefixDropdown = !this.showPrefixDropdown;
  }
  
  selectPrefix(prefix: Prefix, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedPrefix = prefix;
    this.showPrefixDropdown = false;
    
    // Update all phone prefix controls with the selected prefix
    Object.keys(this.bookingForm.controls).forEach(controlName => {
      if (controlName.endsWith('Prefix')) {
        this.bookingForm.get(controlName)?.setValue(prefix.prefix);
        
        // Get the corresponding phone field
        const phoneFieldName = controlName.replace('Prefix', '');
        const phoneControl = this.bookingForm.get(phoneFieldName);
        
        // If the phone control exists and has a value, trigger validation
        if (phoneControl && phoneControl.value) {
          phoneControl.updateValueAndValidity();
        }
      }
    });
  }
  
  selectDate(day: DayInfo): void {
    // Return immediately if the day has no slots
    if (!day.hasSlots) return;
    
    // Reset all days to not selected
    this.weekDays.forEach(d => d.isSelected = false);
    
    // Mark this day as selected
    day.isSelected = true;
    this.selectedDate = day.date;
    
    // Reset time slot when a new date is selected
    this.selectedTimeSlot = null; 
    this.showBookingForm = false; 
    
    // Split time slots into morning and afternoon
    const dateStr = day.date.toDateString();
    const slots = this.timesByDate[dateStr] || [];
    
    this.morningSlots = slots.filter(slot => slot.startTime.getHours() < 12);
    this.afternoonSlots = slots.filter(slot => slot.startTime.getHours() >= 12);
  }
  
  selectTimeSlot(slot: TimeSlot): void {
    this.selectedTimeSlot = slot;
    this.showBookingForm = true;
    
    // If in reschedule mode, check for phone fields and split them
    if (this.isRescheduling) {
      this.handlePhoneFieldsInEditMode();
    }
    
    // Reset prefix to match the form prefix control
    const prefixControl = Object.keys(this.bookingForm.controls).find(key => key.endsWith('Prefix'));
    if (prefixControl) {
      const prefixValue = this.bookingForm.get(prefixControl)?.value || '+1';
      // Find the prefix in the list or default to USA
      this.selectedPrefix = this.prefixes.find(p => p.prefix === prefixValue) || this.prefixes[1];
    }
    
    // Scroll to booking form after a short delay
    setTimeout(() => {
      const element = document.getElementById('booking-form-anchor');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  // Helper method to handle phone fields in edit mode
  handlePhoneFieldsInEditMode(): void {
    const allFields = [...this.defaultFields, ...this.customFields, ...this.leadFields];
    
    allFields.forEach(field => {
      if (field.type === 'tel' && field.prefillValue) {
        const currentValue = this.bookingForm.get(field.id)?.value;
        
        // Check if the value still contains the prefix (hasn't been split yet)
        if (currentValue && currentValue.startsWith('+')) {
          const { prefix, number } = this.splitPhoneNumber(currentValue);
          
          // Update the form controls
          this.bookingForm.get(field.id)?.setValue(number);
          this.bookingForm.get(`${field.id}Prefix`)?.setValue(prefix);
          
          // Update selected prefix for the first phone field
          const matchingPrefix = this.prefixes.find(p => p.prefix === prefix);
          if (matchingPrefix) {
            this.selectedPrefix = matchingPrefix;
          }
        }
      }
    });
  }
  
  // Apply prefill values to form
  applyPrefillValues(): void {
    // Apply prefill values from fields that have them
    const fieldsToProcess = this.isLeadCreationEnabled ? this.leadFields : 
                           [...this.defaultFields, ...this.customFields];
    
    fieldsToProcess.forEach(field => {
      if (field.prefillValue) {
        this.bookingForm.get(field.id)?.setValue(field.prefillValue);
      }
    });
  }
  
  previousWeek(): void {
    const newDate = new Date(this.currentDate);
    newDate.setDate(newDate.getDate() - 7);
    this.currentDate = newDate;
    this.resetSelections();
    this.generateWeekDays();
    this.loadMeetingData();
    this.disableNextButton = false;
  }
  
  nextWeek(): void {
    const newDate = new Date(this.currentDate);
    newDate.setDate(newDate.getDate() + 7);
    this.currentDate = newDate;
    this.resetSelections();
    this.generateWeekDays();
    this.loadMeetingData();
    
  }
  
  private resetSelections(): void {
    this.selectedDate = null;
    this.selectedTimeSlot = null;
    this.showBookingForm = false;
  }
  
  getDurationText(proposedTime: any): string {
    if (!proposedTime) return '30 Minute Meeting';
    return formatDurationText(proposedTime.startTime, proposedTime.endTime);
  }
  
  // Format time only (HH:MM AM/PM)
  formatTimeOnly(date: Date): string {
    if (!date) return '';
    
    const options: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleTimeString(undefined, options);
  }
  
  async confirmBooking() {
    if (this.bookingForm.invalid) {
      // Mark all fields as touched to display validation errors
      Object.keys(this.bookingForm.controls).forEach(key => {
        this.bookingForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.disableButton = true;

    try {
      // Try to submit with v3 first
      await this.submitWithRecaptcha('v3');
    } catch (error: any) {
      console.error('v3 submission failed:', error);
      
      // If v3 failed, try v2
      if (this.recaptchaRequired && error.status === 400) {
        this.snackBar.open('Additional verification required', 'OK', {
          duration: 2000
        });

        try {
          const v2Token = await this.recaptchaService.showCaptchaModal({
            autoClose: true,
            autoCloseDelay: 500
          });

          if (v2Token) {
            await this.submitWithRecaptcha('v2', v2Token);
          } else {
            this.snackBar.open('Verification cancelled', 'OK', {
              duration: 3000,
              panelClass: 'error-snackbar'
            });
          }
        } catch (v2Error) {
          console.error('v2 submission also failed:', v2Error);
          this.snackBar.open('Failed to book the meeting. Please try again.', 'OK', {
            duration: 3000,
            panelClass: 'error-snackbar'
          });
        }
      } else {
        const message = error?.error?.message || 'Failed to book the meeting. Please try again.'
        this.snackBar.open(message, 'OK', {
          duration: 3000,
          panelClass: 'error-snackbar'
        });
      }
    } finally {
      this.disableButton = false;
    }
  }

  private async submitWithRecaptcha(version: 'v3' | 'v2', providedToken?: string): Promise<void> {
    let captchaToken: string | null = null;

    // Get captcha token
    if (this.recaptchaRequired) {
      if (version === 'v3' && this.recaptchaInitialized) {
        // Execute reCAPTCHA v3 with action based on booking type
        const action = this.isRescheduling ? 'reschedule_meeting' : 'book_meeting';
        captchaToken = await this.recaptchaV3Service.execute(action);
      } else {
        captchaToken = providedToken || null;
      }

      if (!captchaToken) {
        throw new Error('No captcha token available');
      }
    }

    const formValues = this.bookingForm.value;
    // Create a map of field IDs to their field objects
    const fieldMap: Record<string, Field> = {};
    
    // Add all fields to the map
    if (this.isLeadCreationEnabled) {
      this.leadFields.forEach(field => {
        fieldMap[field.id] = field;
      });
    } else {
      [...this.defaultFields, ...this.customFields].forEach(field => {
        fieldMap[field.id] = field;
      });
    }

    // Create data object for submission
    let formData: Record<string, any> = {};
    let leadData: Record<string, any> = {};
    
    // Process each form field
    Object.keys(formValues).forEach(fieldId => {
      // Skip prefix fields as they'll be handled separately
      if (fieldId.endsWith('Prefix')) return;
      
      let value = formValues[fieldId];
      const fieldInfo = fieldMap[fieldId];
      
      if (!fieldInfo) return;
      
      // If this is a phone field and has a corresponding prefix
      if (this.bookingForm.get(fieldId) && 
          this.bookingForm.get(`${fieldId}Prefix`) && 
          formValues[fieldId]) {
        // Format phone number with prefix
        value = `${formValues[`${fieldId}Prefix`]}${formValues[fieldId]}`;
      }

      if (fieldInfo.type === 'date' && value) {
        value = new Date(value).toISOString();
      }
      
      // Handle number field conversion
      if (fieldInfo.type === 'number' && value !== '' && value !== null) {
        value = parseFloat(value);
      }
      
      // Collect data based on lead creation mode
      if (this.isLeadCreationEnabled) {
        leadData[fieldInfo.label] = value;
      } else {
        formData[fieldInfo.label] = value;
      }
    });

    let response;
    
    if (this.isRescheduling) {
      // Handle reschedule operation
      const reschedulingData = {
        meetingId: this.meetingId,
        scheduleId: this.scheduleId,
        timeSlot: this.selectedTimeSlot,
        formData: this.isLeadCreationEnabled ? {} : formData,
        userTimezone: this.userTimezone,
        userTimezoneOffset: this.userTimezoneOffset,
        leadData: this.isLeadCreationEnabled ? leadData : null,
        createLead: this.isLeadCreationEnabled,
        recaptchaToken: captchaToken,
        recaptchaAction: 'reschedule_meeting',
        recaptchaVersion: version // Include version info
      };
      response = await firstValueFrom(this.meetingService.rescheduleMeeting(reschedulingData));
    } else {
      // Regular booking operation
      response = await firstValueFrom(this.meetingService.submitMeetingScheduler({
        meeting_id: this.meetingId,
        timeSlot: this.selectedTimeSlot,
        formData: this.isLeadCreationEnabled ? {} : formData,
        leadData: this.isLeadCreationEnabled ? leadData : null,
        createLead: this.isLeadCreationEnabled,
        userTimezone: this.userTimezone,
        userTimezoneOffset: this.userTimezoneOffset,
        rescheduleEvent: this.scheduleId,
        cancelEvent: this.cancelId,
        recaptchaToken: captchaToken,
        recaptchaAction: 'book_meeting',
        recaptchaVersion: version // Include version info
      }));
    }
    
    // Save the meeting data and time slot to session storage
    const submittedFormData = this.isLeadCreationEnabled ? leadData : formData;
    this.saveToSessionStorage(this.meetingData, this.selectedTimeSlot, submittedFormData);
    
    // Redirect to the thank you page
    this.router.navigate([`/meeting-schedule/${this.meetingId}/thank-you-page`]);
  }

  convertToUserTimezone(date: Date): Date {
    if (!this.meetingTimezone || !date) return date;
    
    // If meeting timezone is different from user timezone, convert the time
    if (this.meetingTimezone !== this.userTimezone) {
      try {
        // First convert the date to UTC considering it's in the meeting timezone
        
        // Then convert from UTC to the user's timezone
      } catch (error) {
        console.error('Error converting timezone:', error);
        return date; // Return original date if conversion fails
      }
    }
    
    return date;
  }
  
  // Utility methods for date formatting in thank you page
  formatDate(date: Date): string {
    if (!date) return '';
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString(undefined, options);
  }
  
  formatTime(date: Date): string {
    if (!date) return '';
    
    const options: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleTimeString(undefined, options);
  }

  onCampusChange(campusName: string): void {
    // Find the selected campus
    const selectedCampus = this.allCampuses.find(campus => campus.name === campusName);
    
    if (selectedCampus) {
      // Get programs for this campus
      const programIds = this.campusProgramMapping.get(selectedCampus.id) || [];
      
      // Filter programs based on the selected campus
      this.filteredPrograms = this.allPrograms
        .filter(program => programIds.includes(program.id))
        .map(program => program.name);
      
      // Update the program field options
      this.updateProgramFieldOptions(this.filteredPrograms);
      
      // Clear the program selection if it's not in the filtered list
      this.clearProgramSelectionIfNeeded();
    } else {
      // If no campus selected or campus cleared, show all programs
      this.filteredPrograms = this.allPrograms.map(program => program.name);
      this.updateProgramFieldOptions(this.filteredPrograms);
    }
  }

  updateProgramFieldOptions(programs: string[]): void {
    const programField = [...this.defaultFields, ...this.customFields, ...this.leadFields]
      .find(field => field.label.toLowerCase().includes('program'));
    
    if (programField) {
      programField.options = programs.join(', ');
    }
  }

  clearProgramSelectionIfNeeded(): void {
    const programField = [...this.defaultFields, ...this.customFields, ...this.leadFields]
      .find(field => field.label.toLowerCase().includes('program'));
    
    if (programField) {
      const currentValue = this.bookingForm.get(programField.id)?.value;
      if (currentValue && !this.filteredPrograms.includes(currentValue)) {
        this.bookingForm.get(programField.id)?.setValue('');
      }
    }
  }

  buildCampusProgramMapping(): void {
    this.allCampuses.forEach(campus => {
      const programIds = campus.program_campus.map(pc => pc.program_id);
      this.campusProgramMapping.set(campus.id, programIds);
    });
  }

  dateMinValidator(minDate?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      return null;
      
      // const inputDate = new Date(control.value);
      // const today = new Date();
      // today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      // // Default minimum date is today
      // const minimumDate = minDate ? new Date(minDate) : today;
      
      // if (inputDate < minimumDate) {
      //   return { dateMin: true };
      // }
      
      // return null;
    };
  }
  
  dateMaxValidator(maxDate?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !maxDate) return null;
      
      const inputDate = new Date(control.value);
      const maximumDate = new Date(maxDate);
      
      if (inputDate > maximumDate) {
        return { dateMax: true };
      }
      
      return null;
    };
  }
  
  numberRangeValidator(min?: number, max?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const value = parseFloat(control.value);
      
      if (isNaN(value)) {
        return { pattern: true };
      }
      
      if (min !== undefined && value < min) {
        return { min: true };
      }
      
      if (max !== undefined && value > max) {
        return { max: true };
      }
      
      return null;
    };
  }

  graduationYearValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value && control.value !== 0) return null;
      const val = control.value.toString();
      if (val.includes(',') || val.includes('.')) {
        return { noDecimals: true };
      }
      if (val.replace(/[^0-9]/g, '').length > 4) {
        return { maxDigits: true };
      }
      return null;
    };
  }
  
  isGraduationYearField(field: Field): boolean {
    return field.key === 'highSchoolYearGraduation';
  }
  
  onGraduationYearInput(event: Event, field: Field): void {
    if (field.key !== 'highSchoolYearGraduation') return;
    const input = event.target as HTMLInputElement;
    // Remove commas, dots, and non-digit chars, limit to 4 digits
    let cleaned = input.value.replace(/[^0-9]/g, '');
    if (cleaned.length > 4) {
      cleaned = cleaned.substring(0, 4);
    }
    if (input.value !== cleaned) {
      input.value = cleaned;
      this.bookingForm.get(field.id)?.setValue(cleaned ? parseInt(cleaned, 10) : '');
    }
  }
  
  onGraduationYearKeydown(event: KeyboardEvent): void {
    // Block comma, period, +, -, e
    if ([',', '.', '+', '-', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
  }
}